// src/pages/Accounts/ExpenseRecognitionEntry.js
import React, { useEffect, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  message,
} from "antd";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { fetchRecognitions, saveRecognition } from "./expenseRecognitionsThunk";
import {
  fetchLoadIntoAccs,
  fetchTagGroups,
  fetchTagGroupFields,
  fetchEmployees,
  fetchAccountsByType,
} from "../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  getAccessType,
  getDateTimeFromSelectedDate,
} from "../../lib/functions";
import {
  setEntryDate,
  setIsEditRecognition,
  setSelectedRecognition,
  setRefreshList,
} from "./expenseRecognitionsSlice";
import InputField from "../../components/Input/Input";
import AddNewModal from "../../components/DropDown/AddNewModal";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import { selectAuthInfo } from "../Auth/authSelectors";
import useMigrationDate from "../../hooks/useMigrationDate";
import { fetchHeadAccs } from "../expenses/expenseThunk";

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const ExpenseRecognitionEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  const [rows, setRows] = useState([]);
  const [selectedPayFromAcc, setSelectedPayFromAcc] = useState(null);
  const [selectedEmployeeLocal, setSelectedEmployeeLocal] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  let [access_type, access_type_set] = useState("");
  const [tagModal, setTagModal] = useState(false);

  const authInfo = useSelector(selectAuthInfo);

  const { entryDate, selectedRecognition, isEditRecognition } = useSelector(
    (state) => state.expenseRecognitions
  );


const { headAccs } = useSelector((state) => state.expenses); 
  const { loadIntoAccs, employees,accountsByType } = useSelector((s) => s.lookups);

  useEffect(() => {
    const access = getAccessType("expense_recognition_entry");
    access_type_set(access);
  }, []);

  // initial load
  useEffect(() => {
    dispatch(fetchAccountsByType(["cash_in_hand", "bank_account"]));
    dispatch(fetchHeadAccs());
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(
          groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap())
        )
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      })
      .catch(() => {});
    setRows([
      {
        key: Date.now() + Math.random(),
        to_acc_id: 0,
        acc_name: "",
        description: "",
        amount: 0,
        remark: "",
      },
    ]);
  }, [dispatch, id]);

  // if direct edit route accessed -> load list and set selectedRecognition in store (similar pattern to SupplierPayments)
  useEffect(() => {
    if (id && !selectedRecognition) {
      dispatch(fetchRecognitions())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.recog_id == id);
          if (found) {
            dispatch(setSelectedRecognition(found));
            dispatch(setIsEditRecognition(true));
          }
        })
        .catch(() => {});
    }
  }, [id, selectedRecognition, dispatch]);

  // fill form when edit selected (we avoid calling form.setFieldsValue for everything;
  // we use local state for the controls that matter and only set form values for pure Form items)
  useEffect(() => {
    if (isEditRecognition && selectedRecognition) {
      // set code & entryDate in slice
      dispatch(
        setEntryDate(dayjs(selectedRecognition.created_date).toISOString())
      );

      // tags
      const tagIds = selectedRecognition.tag_field_id
        ? [selectedRecognition.tag_field_id].flat()
        : [];
      setSelectedTags(tagIds);
      form.setFieldsValue({ tags: tagIds });

      // rows (heads)
      setRows(
        (selectedRecognition.details || []).map((d) => ({
          key: d.exp_d_id || Date.now() + Math.random(),
       to_acc_id: d.to_acc_id || 0,  
    acc_name: d.acc_name || "",
          description: d.description || "",
          amount: d.amount || 0,
          remark: d.remark || "",
        }))
      );

      // employee restore (use employees from lookups if available)
      if (selectedRecognition.employee_id) {
        const foundEmp = employees.find(
          (e) =>
            e.employee_id == selectedRecognition.employee_id
        );
        if (foundEmp) {
          setSelectedEmployeeLocal(foundEmp);
          form.setFieldsValue({
            employee_id: {
              sale_id: foundEmp.employee_id,
              display_text: `${foundEmp.employee_name} - ${
                foundEmp.employee_code || ""
              }`,
              raw: foundEmp,
            },
          });
        } else {
          // fallback minimal object
          setSelectedEmployeeLocal({
            employee_id: selectedRecognition.employee_id,
            employee_name: selectedRecognition.employee_name,
            employee_code: selectedRecognition.employee_code || "",
          });
          form.setFieldsValue({
            employee_id: {
              sale_id: selectedRecognition.employee_id,
              display_text:
                selectedRecognition.pay_to || selectedRecognition.employee_name,
              raw: {
                employee_id: selectedRecognition.employee_id,
                employee_name: selectedRecognition.employee_name,
                employee_code: selectedRecognition.employee_code || "",
              },
            },
          });
        }
      }

      // pay from account restore (use loadIntoAccs if available)
 if (selectedRecognition.from_acc_id && accountsByType.length > 0) {
  const foundAcc = accountsByType.find(
    (a) => a.acc_id == selectedRecognition.from_acc_id
  );
  if (foundAcc) {
    setSelectedPayFromAcc(foundAcc);
    form.setFieldsValue({
      from_acc_display: {         // ← must match Form.Item name
        display_text: foundAcc.acc_name,
        sale_id: foundAcc.acc_id,
        raw: foundAcc,
      },
    });
  } else {
    // fallback using from_acc_name from backend join
    setSelectedPayFromAcc({
      acc_id: selectedRecognition.from_acc_id,
      acc_name: selectedRecognition.from_acc_name || "",
    });
    form.setFieldsValue({
      from_acc_display: {
        display_text: selectedRecognition.from_acc_name || "",
        sale_id: selectedRecognition.from_acc_id,
        raw: {
          acc_id: selectedRecognition.from_acc_id,
          acc_name: selectedRecognition.from_acc_name || "",
        },
      },
    });
  }
}

      // set recognition code into slice (we expect a slice action setRecognitionCode)
      dispatch({
        type: "expenseRecognitions/setRecognitionCode",
        payload:
          selectedRecognition.recog_code || selectedRecognition.exp_code || "",
      });
      form.setFieldsValue({
        description: selectedRecognition.narration || "",
        exp_date: dayjs(selectedRecognition.created_date),
      });
    }
  }, [
    isEditRecognition,
    selectedRecognition,
    employees,
    loadIntoAccs,
    form,
    accountsByType,
    dispatch,
  ]);
console.log('selectedRecognition:::',selectedRecognition)
  // add/remove rows
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        to_acc_id: 0,
        acc_name: "",
        amount: 0,
      },
    ]);
  };
  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedRecognition(null));
    dispatch(setIsEditRecognition(false));
    navigate("/accounts/expense-recognitions");
  };

  // Save
  const onFinish = async (values) => {
    // convert selected/entry Date to utc date + time
    let finalEntryDate = entryDate;
    if (!dayjs(entryDate).isSame(dayjs(), "day")) {
      finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ
    } else {
      finalEntryDate = entryDate; // Use now in TZ
    }

    try {
      const totalAmount = rows.reduce(
        (sum, r) => sum + Number(r.amount || 0),
        0
      );

      const nonEmptyRows = rows.filter(
        (r) => r.amount > 0 || r.description?.trim() || r.remark?.trim()
      );
      if (nonEmptyRows.length === 0) {
        modal.error({
          title:
            "Please add at least one row with amount, description, or remark.",
        });
        return;
      }
      if (!values.exp_date && !entryDate) {
        modal.error({ title: "Recognition Date is required!" });
        return;
      }

      if (!selectedPayFromAcc?.acc_id) {
  modal.error({ title: "Please select Pay From Account before saving." });
  return;
}
      for (let r of rows) {
  if (!r.acc_name) {
    modal.error({ title: "Please select Expense Head before saving." });
    return;
  }
  if (r.amount == null || r.amount <= 0) {
    modal.error({ title: "Please enter a valid Amount before saving." });
    return;
  }
}

      const recognitionPayload = {
        recog_id: isEditRecognition
          ? selectedRecognition.recog_id || selectedRecognition.exp_id
          : 0,
        action: isEditRecognition ? "update" : "create",
         from_acc_id: selectedPayFromAcc?.acc_id || null,
        // created_date: (values.exp_date? values.exp_date: dayjs(entryDate)).toISOString(),
        created_date: formatToUtc(finalEntryDate),
        narration: values.description || "",
        total_amount: totalAmount,
      };

      if (selectedTags.length > 0) {
        recognitionPayload.tag_field_id = selectedTags;
      }

      const payload = {
        recognition: recognitionPayload,
        recognitionDetail: rows.map((r) => ({
            to_acc_id: r.to_acc_id || null,
          description: r.description || "",
          remark: r.remark || "",
          amount: Number(r.amount) || 0,
        })),
      };

  
      const res = await dispatch(saveRecognition(payload)).unwrap();
      modal.success({ title: res.msg || "Recognition saved successfully" });

      dispatch(setSelectedRecognition(null));
      dispatch(setIsEditRecognition(false));
      dispatch(setRefreshList(true));

      form.resetFields();
      setRows([]);
      navigate("/accounts/expense-recognitions");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save recognition" });
    }
  };

  // columns for recognition rows
  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
// In the Expense Head column render:
{
  title: "Expense Head",
  dataIndex: "acc_name",
  render: (val, record, index) => (
    <div data-testid={`expense-head-wrapper-${index}`}>
      <SearchListAutoComplete
        entityType="accounts"
        style={{ width: "100%" }}
        optionsList={(headAccs || []).map((acc) => ({
          sale_id: acc.acc_id,
          display_text: acc.acc_name,
          raw: acc,
        }))}
        onSelect={(obj) => {
          const newRows = [...rows];
          newRows[index].to_acc_id = obj?.raw?.acc_id || 0;
          newRows[index].acc_name = obj?.raw?.acc_name || "";
          setRows(newRows);
        }}
        value={
          record.acc_name
            ? { value: record.acc_name, label: record.acc_name }
            : null
        }
        placeholder="Expense Head"
        showAddNew={false}
      />
    </div>
  ),
},
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record, index) => (
        <InputField
          value={record.description}
          data-testid={`description-input-${index}`}
          style={{ width: "100%" }}
          onChange={(e) => {
            const newRows = [...rows];
            newRows[index].description = e.target.value || "";
            setRows(newRows);
          }}
        />
      ),
    },
    // Add to columns array, before "Recognition Amount"

    {
      title: "Recognition Amount",
      dataIndex: "amount",
      width: screens.md ? undefined : 110,
      render: (_, record, index) => (
        <InputNumberField
          data-testid={`recognition-amount-input-${index}`}
          min={0}
          value={record.amount}
          style={{ width: "100%" }}
          step={1} // step by whole number
          precision={2} // restrict to max 2 decimals
          placeholder="0.00"
          onClick={() => {
            const newRows = [...rows];

            if (newRows[index].amount === 0) {
              newRows[index].amount = "";
              setRows(newRows);
            }
          }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].amount = val || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Remark",
      dataIndex: "remark",
      render: (_, record, index) => (
        <InputField
          value={record.remark}
          style={{ width: "100%" }}
          onChange={(e) => {
            const newRows = [...rows];
            newRows[index].remark = e.target.value || "";
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveRow(record.key)} icon={<DeleteOutlined />}>
        </Button>
      ),
    },
  ];

  const { disabledDate } = useMigrationDate();

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Expense Recognition</Title>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1400px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ exp_date: dayjs(entryDate) }}
        onFinish={onFinish}
      >
        <div
          style={{
            display: "flex",
            gap: screens.md && screens.xl ? 80 : 16,
            flexDirection: screens.md && screens.xl ? "row" : "column",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: screens.md && screens.xl ? "40%" : "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Form.Item name="exp_date" label="Recognition Date">
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                disabledDate={disabledDate}
              />
            </Form.Item>
          </div>

          <div
            style={{
              width: screens.md && screens.xl ? "40%" : "100%",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({
                  value: item.id,
                  label: item.combinedField,
                }))}
                onChange={(ids) => setSelectedTags(ids)}
                value={selectedTags}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>

<Form.Item name="from_acc_display" label="Pay From Account" 
  rules={[{ required: true, message: "Please select Pay From Account" }]}>
  <SearchListAutoComplete
    entityType="accounts"
    style={{ width: "100%" }}
    optionsList={(accountsByType  || []).map((acc) => ({
      sale_id: acc.acc_id,
      display_text: acc.acc_name,
      raw: acc,
    }))}
    value={selectedPayFromAcc}
    onSelect={(obj) => {
      setSelectedPayFromAcc(obj?.raw);
      form.setFieldValue("from_acc_display", obj?.display_text);
    }}
    placeholder="Cash / Bank"
    showAddNew={false}
  />
</Form.Item>
          </div>
        </div>

        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
        >
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          style={{ marginTop: 16, marginBottom: 24 }}
          scroll={{ x: "max-content" }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>Total Amount: {Number(totalAmount).toFixed(2)}</strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Common Description">
          <TextArea rows={3} />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
              setSelectedPayFromAcc(null);
              setSelectedEmployeeLocal(null);
              setSelectedTags([]);
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <>
              <Button type="primary" htmlType="submit">
                Save Recognition
              </Button>
            </>
          )}
        </Space>
      </Form>
      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t.raw.field_name.toLowerCase() === createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              return;
            }

            // 🔥 UPDATE OPTIONS FIRST
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists
                ? prev
                : [
                    ...prev,
                    {
                      ...match.raw,
                      combinedField: match.display_text,
                      id: match.id,
                    },
                  ];
            });

            const current = form.getFieldValue("tags") || [];

            if (current.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              return;
            }

            // ✅ NOW auto-select
            form.setFieldsValue({
              tags: [...current, match.id],
            });
            setSelectedTags([...current, match.id]);

            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default ExpenseRecognitionEntry;
